import React, { useEffect, useRef, useState } from 'react';
import {
  listReportTemplates,
  getParcelReportHtml,
  reportHtmlUrl,
} from '../services/api';

/**
 * In-app report viewer modal.
 *
 * Replaces the legacy "Generate Report" link that opened the
 * server-rendered HTML in a new tab. Now we fetch the HTML
 * inline and render it through an iframe `srcDoc` so the
 * report's inline CSS stays sandboxed (a `dangerouslySetInnerHTML`
 * approach would let the report stylesheets bleed into the SPA).
 *
 * The dropdown is data-driven by GET /reports/templates so as new
 * templates land in the backend allowlist they automatically
 * appear here without a frontend change.
 *
 * The "Open in new tab" link is preserved as a fallback so the
 * user can still print or share the report via native browser
 * chrome — same-origin srcDoc reports don't expose Print well.
 */

// localStorage key for the user's last-picked template — restored
// across modal opens so power users don't have to re-pick each time.
const LAST_TEMPLATE_KEY = 'reportViewer.lastTemplate';

function safeReadLastTemplate() {
  try {
    return localStorage.getItem(LAST_TEMPLATE_KEY) || null;
  } catch {
    // localStorage can throw in private mode / disabled storage
    return null;
  }
}

function safeWriteLastTemplate(id) {
  try {
    localStorage.setItem(LAST_TEMPLATE_KEY, id);
  } catch {
    // Ignore — preference loss is harmless
  }
}

export default function ReportViewer({ parcelId, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [html, setHtml] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingHtml, setLoadingHtml] = useState(false);
  const [error, setError] = useState(null);

  // Used to ignore stale fetch responses when the user changes the
  // template before an in-flight HTML fetch resolves.
  const fetchSeqRef = useRef(0);


  // Load template list once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listReportTemplates();
        if (cancelled) return;
        setTemplates(list);
        // Default = last-picked (if still valid) else first entry.
        const last = safeReadLastTemplate();
        const valid = list.find((t) => t.id === last);
        const initial = valid ? valid.id : (list[0] && list[0].id) || null;
        setSelectedId(initial);
      } catch (e) {
        if (!cancelled) setError('Failed to load report templates.');
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch the report HTML whenever parcel/template changes.
  useEffect(() => {
    if (!parcelId || !selectedId) return;
    const seq = ++fetchSeqRef.current;
    setLoadingHtml(true);
    setError(null);
    (async () => {
      try {
        const body = await getParcelReportHtml(parcelId, selectedId);
        // Discard if a newer request superseded this one.
        if (fetchSeqRef.current !== seq) return;
        setHtml(body);
        safeWriteLastTemplate(selectedId);
      } catch (e) {
        if (fetchSeqRef.current !== seq) return;
        setError('Failed to load report. Try a different template or open in a new tab.');
        setHtml('');
      } finally {
        if (fetchSeqRef.current === seq) setLoadingHtml(false);
      }
    })();
  }, [parcelId, selectedId]);

  // Close on Escape — standard modal affordance.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose && onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const newTabHref = selectedId ? reportHtmlUrl(parcelId, selectedId) : '#';

  // On-demand parcelmax Deep-research view (#1093). Replaces the retired
  // "Research the web…" / buyer-brief transcript surface. A plain anchor
  // (not react-router) so this modal stays router-context-free for tests;
  // navigating the SPA to /parcel/:id/research loads the richer flow.
  const deepResearchHref = parcelId
    ? `/parcel/${encodeURIComponent(parcelId)}/research`
    : '#';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Parcel report viewer"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(2px)',
        zIndex: 4000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2vh 2vw',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          width: 'min(1100px, 96vw)',
          height: 'min(960px, 96vh)',
          borderRadius: 8,
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header: title + dropdown + actions */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            borderBottom: '1px solid #e5e7eb',
            background: '#f8fafc',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
            Acquisition report
          </div>
          <label
            htmlFor="report-template-select"
            style={{ fontSize: '0.78rem', color: '#475569', marginLeft: 6 }}
          >
            Template
          </label>
          <select
            id="report-template-select"
            aria-label="Report template"
            disabled={loadingTemplates || templates.length === 0}
            value={selectedId || ''}
            onChange={(e) => setSelectedId(e.target.value)}
            style={{
              padding: '4px 8px',
              fontSize: '0.85rem',
              border: '1px solid #cbd5e1',
              borderRadius: 4,
              background: '#fff',
              minWidth: 180,
            }}
          >
            {loadingTemplates && <option>Loading…</option>}
            {!loadingTemplates && templates.length === 0 && (
              <option>No templates available</option>
            )}
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          {loadingHtml && (
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Rendering…</span>
          )}
          <div style={{ flex: 1 }} />
          {/* Deep-research CTA (#1093). Replaces the retired inline
              "Research the web…" surface with a link to the on-demand
              parcelmax Deep-research view, which produces a far richer
              record (mineral rights, ownership, 45+ verified datapoints). */}
          <a
            href={deepResearchHref}
            className="btn btn-primary"
            title="Open the on-demand deep-research view for this parcel"
            style={{ padding: '4px 10px', fontSize: '0.78rem', textDecoration: 'none' }}
          >
            Deep research →
          </a>
          <a
            href={newTabHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline"
            style={{ padding: '4px 10px', fontSize: '0.78rem', textDecoration: 'none' }}
          >
            Open in new tab
          </a>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close report viewer"
            className="btn btn-outline"
            style={{ padding: '4px 10px', fontSize: '0.78rem' }}
          >
            Close
          </button>
        </div>

        {/* Body: error banner or iframe */}
        <div style={{ flex: 1, position: 'relative', background: '#f1f5f9' }}>
          {error && (
            <div
              role="alert"
              style={{
                margin: 12,
                padding: '10px 14px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#991b1b',
                borderRadius: 6,
                fontSize: '0.85rem',
              }}
            >
              {error}
            </div>
          )}
          {!error && (
            <iframe
              title="Parcel report"
              data-testid="report-iframe"
              srcDoc={html}
              // allow-scripts: the report's inline hydration script
              //   fetches /ai_summary and patches its own DOM (#35).
              //   Safe here because the iframe's HTML comes from our
              //   own backend and is fully autoescaped.
              // allow-same-origin: needed so the embedded /preview.png
              //   loads with the user's auth cookie, and so the
              //   inline fetch can hit /api/v1 without CORS.
              // allow-top-navigation-by-user-activation: lets the report's
              //   "Deep research this parcel →" CTA (target="_top") navigate
              //   the parent SPA to /parcel/:id/research on click (#1093).
              sandbox="allow-scripts allow-same-origin allow-top-navigation-by-user-activation"
              style={{
                width: '100%',
                height: '100%',
                border: '0',
                background: '#fff',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
